import * as React from 'react'
import { AlertTriangle } from 'lucide-react'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { MembershipLevelJoinFormSettings } from '@/pages/MembershipLevelJoinFormSettings'
import { MembershipLevelBasics } from '@/pages/MembershipLevelBasics'
import { MembershipLevelChangeTargetsField } from '@/pages/MembershipLevelChangeTargetsField'
import {
  MembershipLevelProrationSettings,
  RenewalStructureField,
  RenewalTermField,
} from '@/pages/MembershipLevelRenewalFields'
import type {
  MembershipLevelFutureRenewalConfirmation,
  MembershipLevel,
  MembershipLevelForm,
} from '@/pages/MembershipLevelsPage.types'
import type { TaxRate } from '@/pages/TaxSettingsPage'

interface MembershipLevelDialogProps {
  mode: 'new' | 'edit'
  level?: MembershipLevel
  pending: boolean
  error: Error | null
  taxRates: TaxRate[]
  levels: MembershipLevel[]
  onClose: () => void
  onSubmit: (form: MembershipLevelForm) => void
}

export function MembershipLevelDialog({
  mode,
  level,
  pending,
  error,
  taxRates,
  levels,
  onClose,
  onSubmit,
}: MembershipLevelDialogProps) {
  const [form, setForm] = React.useState<MembershipLevelForm>(() =>
    initialMembershipLevelForm(level),
  )
  const fieldErrors = membershipLevelFieldErrors(error)
  const termless = form.renewalStructure === 'Lifetime'
  const fixedDate = form.renewalStructure === 'Fixed date'
  const proratableFixedDate = fixedDate

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit(form)
          }}
        >
          <MembershipLevelDialogHeader mode={mode} level={level} />

          <div className="divide-y divide-border" data-testid="membership-level-editor-sections">
            <MembershipLevelBasics
              form={form}
              mode={mode}
              errors={fieldErrors}
              onChange={setForm}
            />

            <section className="flex flex-col gap-4 py-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <RenewalStructureField form={form} onChange={setForm} />
                <RenewalTermField
                  form={form}
                  disabled={termless}
                  error={fieldErrors.renewalTerm}
                  onChange={setForm}
                />
              </div>
              {fixedDate && (
                <FormField
                  label="Fixed renewal date"
                  htmlFor="membership-level-fixed-renewal-date"
                  error={fieldErrors.fixedRenewalDate}
                  hint="Use MM-DD, such as 01-01."
                >
                  <Input
                    id="membership-level-fixed-renewal-date"
                    value={form.fixedRenewalDate}
                    placeholder="01-01"
                    onChange={(event) => setForm({ ...form, fixedRenewalDate: event.target.value })}
                  />
                </FormField>
              )}
              {proratableFixedDate && (
                <MembershipLevelProrationSettings form={form} onChange={setForm} />
              )}
            </section>

            <section className="flex flex-col gap-4 pt-4">
              <MembershipLevelTaxRateField
                form={form}
                taxRates={taxRates}
                error={fieldErrors.taxRate}
                onChange={setForm}
              />
              <MembershipLevelChangeTargetsField
                form={form}
                level={level}
                levels={levels}
                onChange={setForm}
              />
              <MembershipLevelJoinFormSettings form={form} onChange={setForm} />
            </section>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {membershipLevelSubmitLabel(mode, pending)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function MembershipLevelTaxRateField({
  form,
  taxRates,
  error,
  onChange,
}: {
  form: MembershipLevelForm
  taxRates: TaxRate[]
  error?: string
  onChange: (form: MembershipLevelForm) => void
}) {
  return (
    <FormField label="Tax Rate" htmlFor="membership-level-tax-rate" error={error}>
      <Select
        value={form.taxRateId || 'none'}
        onValueChange={(value) => onChange({ ...form, taxRateId: value === 'none' ? '' : value })}
      >
        <SelectTrigger id="membership-level-tax-rate" aria-label="Tax Rate">
          <SelectValue placeholder="No Tax Rate" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No Tax Rate</SelectItem>
          {taxRates.map((rate) => (
            <SelectItem key={rate.id} value={String(rate.id)}>
              {rate.name} ({rate.percentage}%)
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}

export function FutureRenewalChangeDialog({
  confirmation,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  confirmation: MembershipLevelFutureRenewalConfirmation
  pending: boolean
  error: Error | null
  onClose: () => void
  onConfirm: () => void
}) {
  const { level, form, warning } = confirmation

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{futureRenewalDialogTitle(level, form)}</DialogTitle>
          <DialogDescription>It currently renews as {level.renewal}.</DialogDescription>
        </DialogHeader>
        <Alert variant="warning" className="flex items-start gap-2">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-warning"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <span>
            The new renewal settings apply to{' '}
            <span className="font-medium">future renewals only</span>. The{' '}
            <span className="font-medium">{warning.member_count} members</span> already on this
            level keep their current renewal dates.
          </span>
        </Alert>
        {error && <Alert>{error.message}</Alert>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={onConfirm}>
            {futureRenewalConfirmLabel(level, form)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ArchiveLevelDialog({
  level,
  pending,
  onClose,
  onConfirm,
}: {
  level: MembershipLevel
  pending: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive “{level.name}”?</DialogTitle>
          <DialogDescription>
            It disappears from new assignments but is never deleted. Existing members stay on it and
            its history is kept; you can restore it anytime.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" disabled={pending} onClick={onConfirm}>
            Archive level
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function DeleteLevelDialog({
  level,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  level: MembershipLevel
  pending: boolean
  error: Error | null
  onClose: () => void
  onConfirm: () => void
}) {
  const baseError = apiFieldMessages(error, 'base')[0]

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete “{level.name}” permanently?</DialogTitle>
          <DialogDescription>
            Only levels no member has ever held can be deleted. Levels with history should be
            archived instead.
          </DialogDescription>
        </DialogHeader>
        {baseError && <Alert>{baseError}</Alert>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" disabled={pending} onClick={onConfirm}>
            Delete level
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function MembershipLevelDialogHeader({
  mode,
  level,
}: {
  mode: 'new' | 'edit'
  level?: MembershipLevel
}) {
  return (
    <DialogHeader>
      <DialogTitle>{membershipLevelDialogTitle(mode, level)}</DialogTitle>
    </DialogHeader>
  )
}

function initialMembershipLevelForm(level?: MembershipLevel): MembershipLevelForm {
  if (!level) {
    return {
      name: '',
      kind: 'individual',
      rosterCap: '',
      description: '',
      fee: '',
      renewalStructure: 'Anniversary',
      renewalTerm: 'Yearly',
      fixedRenewalDate: '01-01',
      publishedToJoinForm: false,
      requiresAdminApproval: false,
      prorateFirstPartialPeriod: false,
      taxRateId: '',
      levelChangeTargetIds: [],
    }
  }

  return {
    name: level.name,
    kind: level.kind ?? 'individual',
    rosterCap: level.roster_cap == null ? '' : String(level.roster_cap),
    description: level.description ?? '',
    fee: feeInputValue(level),
    renewalStructure: level.renewal_structure,
    renewalTerm: level.renewal_term ?? 'Yearly',
    fixedRenewalDate: level.fixed_renewal_date ?? '01-01',
    publishedToJoinForm: level.published_to_join_form,
    requiresAdminApproval: level.requires_admin_approval,
    prorateFirstPartialPeriod: level.prorate_first_partial_period,
    taxRateId: level.tax_rate ? String(level.tax_rate.id) : '',
    levelChangeTargetIds: (level.level_change_targets ?? []).map((target) => target.id),
  }
}

function membershipLevelFieldErrors(error: Error | null) {
  return {
    name: apiFieldMessages(error, 'name')[0],
    kind: apiFieldMessages(error, 'kind')[0],
    rosterCap: apiFieldMessages(error, 'roster_cap')[0],
    fee: apiFieldMessages(error, 'fee')[0],
    renewalTerm: apiFieldMessages(error, 'renewal_term')[0],
    fixedRenewalDate: apiFieldMessages(error, 'fixed_renewal_date')[0],
    taxRate: apiFieldMessages(error, 'tax_rate')[0],
  }
}

function membershipLevelDialogTitle(mode: 'new' | 'edit', level?: MembershipLevel) {
  if (mode === 'edit') return `Edit level — ${level?.name ?? 'level'}`

  return 'New level'
}

function membershipLevelSubmitLabel(mode: 'new' | 'edit', pending: boolean) {
  if (pending) return 'Saving...'
  if (mode === 'edit') return 'Save changes'

  return 'Create level'
}

function feeInputValue(level: MembershipLevel) {
  if (level.free) return '0'

  return level.fee.replace(/^[^\d-]+/, '')
}

function futureRenewalDialogTitle(level: MembershipLevel, form: MembershipLevelForm) {
  if (level.renewal_structure !== form.renewalStructure) {
    return `Change ${level.name} to ${form.renewalStructure} renewal?`
  }

  return `Change ${level.name} renewal term?`
}

function futureRenewalConfirmLabel(level: MembershipLevel, form: MembershipLevelForm) {
  if (level.renewal_structure !== form.renewalStructure) return 'Change structure'

  return 'Change term'
}
